'use client';

import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import InlineLoadingBar from 'src/components/InlineLoadingBar/InlineLoadingBar';
import useReferralsPageData from 'src/components/ReferralsPageContent/hooks/useReferralsPageData';
import NoReferralLinkCard from 'src/components/ReferralsPageContent/NoReferralLinkCard';
import ReferralChart from 'src/components/ReferralsPageContent/ReferralChart';
import ReferralsPageChartSection from 'src/components/ReferralsPageContent/ReferralsPageChartSection';
import ReferralsPageStatsCard from 'src/components/ReferralsPageContent/ReferralsPageStatsCard';
import { ReferralTimePeriodOption } from 'src/components/ReferralsPageContent/consts';
import { cj } from 'src/utils/classNames';

const STANDARD_COL_SPAN = 'col-span-12 sm:col-span-6';

export default function ReferralsPageContent() {
	const [selectedPeriod, setSelectedPeriod] =
		useSyncLocalStorage<ReferralTimePeriodOption>('referralsPagePeriod', '7d');

	const {
		isLoading,
		isFetching,
		totalRewards,
		totalNumReferrals,
		rewardsDeltaForPeriod,
		referrerEarnPercent,
		referralLink,
		deletedFirstAccount,
		referralLinkExistsForAccount,
		rewardsChartData,
		numReferralsChartData,
		numReferralsDeltaForPeriod,
	} = useReferralsPageData({ period: selectedPeriod });

	if (isLoading) {
		return (
			<div className="flex h-32 items-center justify-center">
				<InlineLoadingBar />
			</div>
		);
	}

	if (deletedFirstAccount) {
		return (
			<p className="typo-b3 text-text-tertiary">
				Unfortunately you have deleted your first account and therefore cannot
				create a referral link. If you wish to still create a referral link, you
				may create a new Drift account using another wallet.
			</p>
		);
	}

	if (!referralLinkExistsForAccount) {
		return (
			<NoReferralLinkCard
				// We override styles here as otherwise the card is too wide on this page.
				className="max-w-158 self-center"
				imageClassName="sm:w-72 shrink-0"
				contentClassName="sm:flex-col sm:gap-y-2xl sm:items-start"
			/>
		);
	}

	return (
		<div
			className="grid grid-cols-12 gap-x-lg gap-y-lg pb-2xl sm:gap-y-2xl"
			data-testid="referrals-page-content"
		>
			<ReferralsPageStatsCard
				className="col-span-12"
				totalRewards={totalRewards}
				totalNumReferrals={totalNumReferrals}
				referrerEarnPercent={referrerEarnPercent}
				referralLink={referralLink}
				isLoading={isLoading}
			/>

			<ReferralsPageChartSection
				title="Rewards Earned"
				className={STANDARD_COL_SPAN}
			>
				<ReferralChart
					className={cj(
						'min-h-61 grow sm:min-h-64',
						isFetching && 'animate-pulse'
					)}
					label="Rewards"
					headerValue={rewardsDeltaForPeriod}
					data={rewardsChartData}
					selectedPeriod={selectedPeriod}
					setSelectedPeriod={setSelectedPeriod}
					isLoading={isLoading}
					isNotional
				/>
			</ReferralsPageChartSection>

			<ReferralsPageChartSection
				title="Referrals"
				className={STANDARD_COL_SPAN}
			>
				<ReferralChart
					className={cj(
						'min-h-61 grow sm:min-h-64',
						isFetching && 'animate-pulse'
					)}
					type="bar"
					label="Referrals"
					headerValue={numReferralsDeltaForPeriod}
					data={numReferralsChartData}
					selectedPeriod={selectedPeriod}
					setSelectedPeriod={setSelectedPeriod}
					isLoading={isLoading}
				/>
			</ReferralsPageChartSection>
		</div>
	);
}
